"use client"

import { notify } from "@/lib/notifications/notify"
import { SendMark } from "@/components/ui/send-mark"
import { useState, useEffect, useRef } from "react"
import Image from "next/image"
import Link from "next/link"
import { useRouter, usePathname } from "next/navigation"
import {
  MapPin, Instagram, MessageCircle,
  UserPlus, UserCheck,
  Loader2, ChevronLeft, ChevronRight,
  LayoutGrid, Rows, Square, Building, Share2, Phone, MessageSquare, Globe, Facebook,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { createClient } from "@/lib/supabase/client"
import { toast } from "sonner"
import { PublicNavbar } from "@/components/public-navbar"
import { PageLoader } from "@/components/page-loader"
import { FollowersModal } from "@/components/network/followers-modal"
import { InquireModal } from "@/components/network/inquire-modal"
import { useUserRole } from "@/providers/user-role-provider"
import { StorefrontBooking } from "@/components/booking/storefront-booking"
import { ShopHoursBanner } from "@/components/booking/shop-hours-banner"
import { NextAvailabilityBadge } from "@/components/booking/next-availability-badge"
import { ClientFooter } from "@/components/client/client-footer"
import { getOpenState, formatTime, DAY_NAMES, type ShopHour } from "@/lib/shop-hours"
// Social profile fields only. These strip query strings, so they must never
// be used on booking links — those carry ?src= for channel attribution.
import { normalizeInstagramUrl, normalizeTikTokUrl, normalizeYouTubeUrl, normalizeXUrl, normalizeFacebookUrl } from "@/lib/social-urls"
import { buildBarberSocialEmbedSlides } from "@/lib/social-embeds"
import { BarberSocialEmbedRail } from "@/components/barber/barber-social-embed-rail"
import { displayLocation } from "@/lib/format-location"
import { TikTokIcon, XIcon, YouTubeIcon } from "@/components/icons/social"

// Official WhatsApp glyph (lucide has none) — same path used in the booking modal.
function WhatsAppIcon({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
    </svg>
  )
}

// Match the inline view's pastel shop palette + black icon set
const SHOP_PASTELS: { bg: string; fg: string; ring: string }[] = [
  { bg: "bg-amber-50",   fg: "text-amber-700",   ring: "border-amber-100" },
  { bg: "bg-emerald-50", fg: "text-emerald-700", ring: "border-emerald-100" },
  { bg: "bg-sky-50",     fg: "text-sky-700",     ring: "border-sky-100" },
  { bg: "bg-rose-50",    fg: "text-rose-700",    ring: "border-rose-100" },
  { bg: "bg-violet-50",  fg: "text-violet-700",  ring: "border-violet-100" },
  { bg: "bg-orange-50",  fg: "text-orange-700",  ring: "border-orange-100" },
  { bg: "bg-pink-50",    fg: "text-pink-700",    ring: "border-pink-100" },
  { bg: "bg-teal-50",    fg: "text-teal-700",    ring: "border-teal-100" },
  { bg: "bg-indigo-50",  fg: "text-indigo-700",  ring: "border-indigo-100" },
  { bg: "bg-fuchsia-50", fg: "text-fuchsia-700", ring: "border-fuchsia-100" },
]

function ScissorsVerticalIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <circle cx="7" cy="18" r="2.5" />
      <circle cx="17" cy="18" r="2.5" />
      <path d="M8.8 16.2 19 3" />
      <path d="M15.2 16.2 5 3" />
      <path d="M12 12 12 9" />
    </svg>
  )
}

const AVAILABILITY_LABELS: Record<string, string> = {
  full_time: "Full-time",
  part_time: "Part-time",
  weekdays_only: "Weekdays only",
  weekends_only: "Weekends only",
  flexible: "Flexible",
}

const CLIENTELE_LABELS: Record<string, string> = {
  building: "Building clientele",
  established: "Established clientele",
  large_following: "Large following",
  walk_in_ready: "Walk In Ready",
}

const PAYMENT_LABELS: Record<string, string> = {
  booth_rental: "Booth rental",
  commission: "Commission",
  both: "Booth rental or commission",
}

const BOOKING_LABELS: Record<string, string> = {
  squire: "Squire",
  booksy: "Booksy",
  thecut: "The Cut",
  fresha: "Fresha",
  other: "Book online",
}

const BOOKING_LOGOS: Record<string, string> = {
  squire: "/booking-logos/squire.svg",
  booksy: "/booking-logos/booksy.svg",
  thecut: "/booking-logos/thecut.svg",
  fresha: "/booking-logos/fresha.svg",
}

// Client-side fallback so legacy profiles (saved before booking_platform
// existed, or with a stale 'other' value) still light up the right logo
// based purely on the URL.
function detectBookingPlatformFromUrl(url: string | null | undefined): string {
  const lower = (url || "").toLowerCase()
  if (lower.includes("getsquire") || lower.includes("squireapp") || lower.includes("squire.com")) return "squire"
  if (lower.includes("booksy")) return "booksy"
  if (lower.includes("thecut")) return "thecut"
  if (lower.includes("fresha")) return "fresha"
  return ""
}

interface ShopExperience {
  id: string
  shop_id?: string | null
  shop_name: string
  city: string | null
  state: string | null
  start_year: number | null
  end_year: number | null
  is_current: boolean
  shop?: {
    id: string
    slug: string | null
    name: string
    cover_image: string | null
    shop_type: string | null
    city: string | null
    state: string | null
    deleted_at: string | null
  } | null
}

interface PortfolioImage {
  id: string
  image_url: string
  caption: string | null
}

interface Props {
  profile: any
  experience: ShopExperience[]
  portfolio: PortfolioImage[]
  followersCount: number
  // Owner-only: surfaced as a small strip under the header when an owner views
  // their roster barber. Omitted everywhere else (community/hiring/self).
  ownerStats?: { totalPaid: number; memberSince: string } | null
  // True when rendered INSIDE the dashboard layout (owner barber view). The
  // dashboard already provides chrome + top/left padding, so the component
  // skips its own navbars and double top padding to avoid two stacked navbars.
  embedded?: boolean
  // Booking lens (client/public viewer): adds the contact row, Work Locations
  // schedule, and the booking flow under the portfolio. Off for community/owner.
  bookingLens?: boolean
  barberPhone?: string | null
  services?: { id: string; name: string; description?: string | null; duration_minutes: number; price_cents: number; currency?: string | null; deposit_cents?: number | null }[]
  memberships?: { id: string; name: string; description?: string | null; price_cents: number; interval?: string | null; currency?: string | null }[]
  shopHours?: ShopHour[]
  activeShop?: { id: string; slug: string | null; name: string | null; cover_image: string | null; city: string | null; state: string | null } | null
  acceptsOnlinePayments?: boolean
  depositsEnabled?: boolean
  passProcessingToClient?: boolean
}

export default function BarberPublicProfile({ profile, experience, portfolio, followersCount, ownerStats, embedded, bookingLens, barberPhone, services = [], memberships = [], shopHours = [], activeShop, acceptsOnlinePayments = false, depositsEnabled = false, passProcessingToClient = false }: Props) {
  const router = useRouter()
  const pathname = usePathname()
  const supabase = createClient()
  // Active MODE (persisted in localStorage) — drives in-app navigation chrome
  // so a dual-role user in barber mode returns to the barber surfaces, not
  // owner. The DB role (currentUserRole) still gates owner-only features.
  const { role: currentMode, isClient } = useUserRole()
  // `profile.profiles` (the joined auth/profile row) can be null — the booking
  // link route (/book/[slug]) explicitly passes null when the join is empty,
  // and the embed can resolve null for an orphaned barber_profiles row. Fall
  // back to the barber_profiles id (same value — shared primary key) so the
  // always-rendered modals below (FollowersModal/InquireModal) never crash the
  // whole page on `barberProfile.id`. Display code already coalesces the name.
  const barberProfile = (profile.profiles ?? { id: profile.id, full_name: "", avatar_url: "" }) as { id: string; full_name: string; avatar_url: string }

  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null)
  const [authChecked, setAuthChecked] = useState(false)
  const [isFollowing, setIsFollowing] = useState(false)
  const [followerCount, setFollowerCount] = useState(followersCount)
  const [followLoading, setFollowLoading] = useState(false)
  const [followersOpen, setFollowersOpen] = useState(false)
  const [inquireOpen, setInquireOpen] = useState(false)
  const [ownerShop, setOwnerShop] = useState<{ id: string; name: string; city: string | null; state: string | null } | null>(null)
  const [lightboxImage, setLightboxImage] = useState<string | null>(null)
  const [portfolioView, setPortfolioView] = useState<"grid3" | "grid2" | "single">("grid3")
  const [subscribingId, setSubscribingId] = useState<string | null>(null)
  const carouselRef = useRef<HTMLDivElement>(null)

  // Subscribe the viewing client to one of the barber's membership plans →
  // Stripe subscription checkout (see /api/memberships/subscribe). Logged-out
  // viewers are sent to sign up first, then back here.
  async function subscribeMembership(id: string) {
    if (!isLoggedIn) {
      router.push(`/auth/signup?redirect=${encodeURIComponent(pathname || "/")}`)
      return
    }
    setSubscribingId(id)
    try {
      const res = await fetch("/api/memberships/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ membership_id: id, return_path: pathname }),
      })
      const data = await res.json()
      if (!res.ok || !data.url) {
        toast.error(data.error || "Couldn't start checkout")
        setSubscribingId(null)
        return
      }
      window.location.href = data.url
    } catch {
      toast.error("Couldn't start checkout")
      setSubscribingId(null)
    }
  }

  // In the booking lens (Client mode / guest funnel) the viewer is a CUSTOMER,
  // never the owner — so a pro viewing their own profile in Client mode gets the
  // full client experience (booking flow, follow/share/contact) and no Edit.
  const isOwnProfile = currentUserId === barberProfile?.id && !bookingLens
  const isLoggedIn = authChecked && !!currentUserId
  // Clients also have the floating nav, so they need the same left/top clearance
  // (currentUserRole is null for clients — without isClient the content slid
  // under the floating launcher at tablet widths).

  useEffect(() => {
    async function init() {
      // Each step is wrapped so one failed query can't strand the whole
      // page on the loading spinner. We've been burned by this before:
      // an RLS deny on the profile_view notification insert was enough
      // to leave the entire profile blank.
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) return
        setCurrentUserId(user.id)

        try {
          const { data: p } = await supabase.from("profiles").select("role").eq("id", user.id).maybeSingle()
          setCurrentUserRole(p?.role || null)

          if (p?.role === "owner") {
            try {
              const { data: shops } = await supabase
                .from("shops")
                .select("id, name, city, state")
                .eq("owner_id", user.id)
                .eq("is_published", true)
                .limit(1)
              if (shops?.[0]) setOwnerShop(shops[0])
            } catch (e) { console.warn("[barber-profile] owner shop lookup failed", e) }
          }
        } catch (e) { console.warn("[barber-profile] role lookup failed", e) }

        try {
          // In the booking lens (clients + pros in Client mode) a follow is a
          // personal "save" → saved_barbers, since clients have no profiles row
          // and can't use the barber↔barber network_follows graph.
          if (bookingLens) {
            const { data: saved } = await supabase
              .from("saved_barbers")
              .select("id")
              .eq("user_id", user.id)
              .eq("barber_id", barberProfile.id)
              .maybeSingle()
            setIsFollowing(!!saved)
          } else {
            const { data: follow } = await supabase
              .from("network_follows")
              .select("id")
              .eq("follower_id", user.id)
              .eq("following_id", barberProfile.id)
              .maybeSingle()
            setIsFollowing(!!follow)
          }
        } catch (e) { console.warn("[barber-profile] follow lookup failed", e) }

        if (user.id !== barberProfile.id) {
          try {
            await notify({
              recipient: barberProfile.id,
              type: "profile_view",
              metadata: { viewer_id: user.id },
            })
          } catch (e) { console.warn("[barber-profile] profile_view insert failed", e) }
        }
      } catch (e) {
        console.warn("[barber-profile] init failed", e)
      }
    }
    // .finally so the page always reveals, even if init throws unexpectedly
    init().finally(() => setAuthChecked(true))
  }, [])

  const handleFollow = async () => {
    if (!currentUserId) { router.push("/auth/login"); return }
    setFollowLoading(true)
    // Booking lens (clients / Client mode) → personal save in saved_barbers.
    // Community lens (pros) → the barber↔barber network_follows graph.
    if (bookingLens) {
      if (isFollowing) {
        await supabase.from("saved_barbers").delete().eq("user_id", currentUserId).eq("barber_id", barberProfile.id)
        setIsFollowing(false)
      } else {
        await supabase.from("saved_barbers").insert({ user_id: currentUserId, barber_id: barberProfile.id })
        setIsFollowing(true)
      }
      setFollowLoading(false)
      return
    }
    if (isFollowing) {
      await supabase.from("network_follows").delete().eq("follower_id", currentUserId).eq("following_id", barberProfile.id)
      setIsFollowing(false)
      setFollowerCount((c: number) => c - 1)
    } else {
      await supabase.from("network_follows").insert({ follower_id: currentUserId, following_id: barberProfile.id })
      setIsFollowing(true)
      setFollowerCount((c: number) => c + 1)
      await notify({
        recipient: barberProfile.id,
        type: "new_follower",
        metadata: { follower_id: currentUserId },
      })
    }
    setFollowLoading(false)
  }


  const handleMessage = () => {
    if (!currentUserId) { router.push("/auth/login"); return }
    const dashPath = currentMode === "owner" ? "owner" : "barber"
    router.push(`/dashboard/${dashPath}/messages?userId=${barberProfile.id}`)
  }

  const handleShare = async () => {
    if (typeof window === "undefined") return
    const url = window.location.href
    try {
      if (navigator.share) {
        await navigator.share({ title: `${name} | BOOTH`, url })
      } else {
        await navigator.clipboard.writeText(url)
        toast.success("Profile link copied")
      }
    } catch { /* user cancelled share */ }
  }

  const handleInviteToApply = () => {
    if (!currentUserId) { router.push("/auth/login"); return }
    setInquireOpen(true)
  }

  const name = barberProfile?.full_name || "Barber"
  const location = displayLocation(profile.city, profile.state)
  // A social only counts as "present" when it points somewhere real. Beyond
  // null / empty / whitespace, we also reject scheme- or prefix-only leftovers
  // — "https://", "http://", "www." — which a half-filled-then-cleared field
  // (or legacy import) can leave behind. Those are non-blank, so they'd sneak
  // past a plain truthiness check and render a dead icon linking nowhere.
  const filled = (v: unknown): string | null => {
    if (typeof v !== "string") return null
    const t = v.trim()
    if (!t) return null
    const host = t.replace(/^https?:\/\//i, "").replace(/^www\.?/i, "").trim()
    return host ? t : null
  }
  const hasSocials =
    filled(profile.instagram_url) || filled(profile.tiktok_url) || filled(profile.x_url) ||
    filled(profile.youtube_url) || filled(profile.facebook_url) || filled(profile.booking_url)
  const socialEmbedSlides = buildBarberSocialEmbedSlides(profile)
  const hasDetails = profile.availability || profile.payment_type_preference || profile.clientele_size

  if (!authChecked) {
    return (
      <PageLoader />
    )
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Lightbox — dark backdrop with the image rendered at its natural
          aspect ratio (no card frame, no max-w cap, no aspect-square
          letterbox). Clicks on the image itself don't dismiss so users
          can inspect details before closing. */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 sm:p-8 cursor-zoom-out animate-in fade-in duration-200"
          onClick={() => setLightboxImage(null)}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={lightboxImage}
            alt=""
            className="max-w-[95vw] max-h-[95vh] w-auto h-auto object-contain rounded-lg animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* No per-page navbar: the global FloatingNav covers signed-in users at
          every width. The old md→lg DashboardNavbarMenu was double-rendering a
          second navbar on top of it, so it's removed. */}

      {/* Unauthenticated header — site-wide PublicNavbar (public protocol).
          PublicNavbar self-suppresses for signed-in users on non-landing
          routes, where the global FloatingNav is the chrome instead. */}
      {authChecked && !currentUserId && !embedded && <PublicNavbar />}

      {/* Lightbox overlay (already on top of everything) */}

      {/* Page content — a centred max-w-3xl column in both auth states. The
          only difference left is the top offset: signed-in viewers clear the
          floating wordmark and bell, logged-out viewers sit under
          PublicNavbar. */}
      <div className="flex-1">
        {/* Centered on the true viewport for both signed-in and public views —
            the centered max-w-3xl column already clears the floating nav. */}
        <div className={`max-w-3xl mx-auto pb-28 px-6 lg:px-8 ${embedded ? "pt-16 md:pt-2 lg:pt-2" : "pt-16 md:pt-20"}`}>

          {/* NO BACK CONTROL HERE. The signed-in chrome already puts one in
              the top-left corner — components/top-left-action.tsx, which
              renders Back below a root and routes through lib/nav-back.

              This page used to draw its own as well, with the same
              `isLoggedIn` condition as that pill, so the two were exactly
              coincident: every signed-in visitor on a phone saw two Back
              controls stacked down the left edge, the floating pill and an
              arrow-and-label underneath it.

              The pill is the one that stays — it is on every route, it knows
              where a root is, and it handles the cold-open case (a push
              notification opening this profile with no history to pop) that
              the local button was duplicating by hand. */}

          {/* Heading + everything below is inset slightly so it reads as
              the content body — uniform left edge for Barber Profile,
              avatar, name, links, portfolio. */}
          {/* Eyebrow row — Next-availability badge sits up here (not on the name
              row) so it never squeezes / truncates the barber's name. */}
          <div className="flex items-center justify-between gap-3 mb-5">
            <h1 className="text-sm font-medium text-foreground">Barber Profile</h1>
            {bookingLens && profile.bookings_enabled && (
              <div className="flex-shrink-0">
                <NextAvailabilityBadge barberId={profile.id} />
              </div>
            )}
          </div>

          <div className="space-y-10">
            {/* Header: avatar + name + location */}
            <div className="flex items-start gap-4">
              <Avatar className="w-20 h-20 border border-border flex-shrink-0">
                <AvatarImage src={barberProfile?.avatar_url || ""} />
                <AvatarFallback className="bg-muted text-2xl">{name.charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <h2 className="text-xl font-bold truncate">{name}</h2>
                  {isOwnProfile && (
                    <Link
                      href="/dashboard/barber/profile?edit=true"
                      className="text-sm text-muted-foreground hover:text-blue-600 transition-colors flex-shrink-0 mt-1"
                    >
                      Edit
                    </Link>
                  )}
                </div>
                {location && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3.5 h-3.5" />{location}
                  </p>
                )}
                {profile.years_experience != null && (
                  <span className="inline-flex mt-1.5 text-[11px] font-normal bg-muted text-foreground border border-border px-2.5 py-0.5 rounded-full whitespace-nowrap tabular-nums">
                    {profile.years_experience}Y EXP
                  </span>
                )}
              </div>
            </div>

            {/* Owner-only stats strip (only when an owner views their barber). */}
            {ownerStats && (
              <div className="flex items-center gap-8">
                <div>
                  <p className="text-base font-semibold text-emerald-600">${ownerStats.totalPaid}</p>
                  <p className="text-[11px] text-muted-foreground">Total paid</p>
                </div>
                <div>
                  <p className="text-base font-semibold text-foreground">{ownerStats.memberSince}</p>
                  <p className="text-[11px] text-muted-foreground">Member since</p>
                </div>
              </div>
            )}

            {/* Identity cluster — follow/share row, stats, contact actions and
                About grouped under a tighter space-y-4: the page-level
                space-y-10 read as dead air between these short related rows.
                -mt-6 pulls the cluster up so the follow/share row sits close
                under the avatar instead of floating below a 40px gap. */}
            <div className="space-y-4 -mt-6">
            {/* Row beneath the header: follow/share chip on the left + hiring
                badges (owner-only) flowing alongside. Hidden entirely when
                neither has anything to render. */}
            {(() => {
              // availability + clientele_size are TEXT[]; tolerate legacy single-string rows.
              const toArr = (v: unknown): string[] =>
                Array.isArray(v) ? (v as string[]).filter(Boolean) : v ? [v as string] : []
              const availabilityList = toArr(profile.availability)
              const clienteleList = toArr(profile.clientele_size)
              // Hiring lens = viewer is in OWNER mode (recruiting context). In
              // barber/community mode (incl. dual-role users browsing the team
              // roster) these recruiting badges are hidden — the profile reads
              // as a peer/community profile. Gated by active MODE, not DB role,
              // so a dual-role owner in barber mode also gets the community lens.
              // AND never in the booking lens (Client mode / guest funnel): a
              // dual-role owner still carries currentMode==="owner" while browsing
              // as a customer, so without !bookingLens the hiring signals leaked
              // into the client view.
              const showHiringBadges = currentMode === "owner" && !bookingLens && (
                availabilityList.length > 0 || profile.is_open_to_work || clienteleList.length > 0 || profile.payment_type_preference
              )
              if (isOwnProfile && !showHiringBadges) return null
              return (
                <div className="flex flex-wrap items-center gap-2">
                  {!isOwnProfile && (
                    <div className="inline-flex items-center gap-1 rounded-full border border-border bg-card p-0.5">
                      <button
                        onClick={handleFollow}
                        disabled={followLoading}
                        className="inline-flex items-center justify-center w-[26px] h-[26px] rounded-full text-emerald-500 hover:text-emerald-600 transition-colors disabled:opacity-40"
                        aria-label={isFollowing ? "Unfollow" : "Follow"}
                      >
                        {followLoading
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : isFollowing
                            ? <UserCheck className="w-3.5 h-3.5" strokeWidth={2} />
                            : <UserPlus className="w-3.5 h-3.5" strokeWidth={2} />}
                      </button>
                      <span className="w-px h-4 bg-border" />
                      <button
                        onClick={handleShare}
                        className="inline-flex items-center justify-center w-[26px] h-[26px] rounded-full text-muted-foreground hover:text-foreground transition-colors"
                        aria-label="Share profile"
                      >
                        <Share2 className="w-3.5 h-3.5" strokeWidth={2} />
                      </button>
                    </div>
                  )}
                  {showHiringBadges && (
                    <>
                      {availabilityList.map(a => (
                        <span key={`avail-${a}`} className="text-[11px] font-light bg-amber-50 text-amber-700 border border-amber-100 px-3 py-1 rounded-full">
                          {AVAILABILITY_LABELS[a] || a}
                        </span>
                      ))}
                      {profile.is_open_to_work && (
                        <span className="text-[11px] font-light bg-emerald-50 text-emerald-700 border border-emerald-100 px-3 py-1 rounded-full">Open to work</span>
                      )}
                      {clienteleList.map(c => (
                        <span key={`cli-${c}`} className="text-[11px] font-light bg-violet-50 text-violet-700 border border-violet-100 px-3 py-1 rounded-full">
                          {CLIENTELE_LABELS[c] || c}
                        </span>
                      ))}
                      {profile.payment_type_preference && (
                        <span className="text-[11px] font-light bg-teal-50 text-teal-700 border border-teal-100 px-3 py-1 rounded-full">
                          {PAYMENT_LABELS[profile.payment_type_preference]}
                        </span>
                      )}
                    </>
                  )}

                  {/* Social links — right end of the follow/share row. */}
                  {hasSocials && (
                    <div className="ml-auto flex items-center gap-3">
                      {filled(profile.booking_url) && (() => {
                        // The URL is the source of truth. A stale stored
                        // booking_platform (e.g. 'squire' left over after the
                        // link was changed to a plain website) must NOT stamp a
                        // booking logo onto a website URL — a real platform link
                        // (getsquire.com, booksy…) is recognized from the URL;
                        // anything else (webflow.io) falls through to the globe.
                        const platform = detectBookingPlatformFromUrl(profile.booking_url)
                        const logo = BOOKING_LOGOS[platform]
                        return (
                          <a href={profile.booking_url} target="_blank" rel="noopener noreferrer"
                            className="text-foreground hover:opacity-70 transition-opacity inline-flex items-center"
                            aria-label={BOOKING_LABELS[platform] || "Booking link"}>
                            {logo ? (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img src={logo} alt={BOOKING_LABELS[platform] || "Booking"} className="h-6 w-auto" />
                            ) : (
                              // Generic website (not a recognized booking
                              // platform) → thin globe, not scissors: the
                              // account-settings field is labeled "Website".
                              <Globe className="w-5 h-5" strokeWidth={1.25} />
                            )}
                          </a>
                        )
                      })()}
                      {normalizeInstagramUrl(profile.instagram_url) && (
                        <a href={normalizeInstagramUrl(profile.instagram_url)!} target="_blank" rel="noopener noreferrer"
                          className="text-foreground hover:text-blue-600 transition-colors" aria-label="Instagram">
                          <Instagram className="w-5 h-5" strokeWidth={1.5} />
                        </a>
                      )}
                      {normalizeFacebookUrl(profile.facebook_url) && (
                        <a href={normalizeFacebookUrl(profile.facebook_url)!} target="_blank" rel="noopener noreferrer"
                          className="text-foreground hover:text-blue-600 transition-colors" aria-label="Facebook">
                          <Facebook className="w-5 h-5" strokeWidth={1.5} />
                        </a>
                      )}
                      {normalizeTikTokUrl(profile.tiktok_url) && (
                        <a href={normalizeTikTokUrl(profile.tiktok_url)!} target="_blank" rel="noopener noreferrer"
                          className="text-foreground hover:text-blue-600 transition-colors" aria-label="TikTok">
                          <TikTokIcon className="w-5 h-5" />
                        </a>
                      )}
                      {normalizeXUrl(profile.x_url) && (
                        <a href={normalizeXUrl(profile.x_url)!} target="_blank" rel="noopener noreferrer"
                          className="text-foreground hover:text-blue-600 transition-colors" aria-label="X">
                          <XIcon className="w-5 h-5" />
                        </a>
                      )}
                      {normalizeYouTubeUrl(profile.youtube_url) && (
                        <a href={normalizeYouTubeUrl(profile.youtube_url)!} target="_blank" rel="noopener noreferrer"
                          className="text-foreground hover:text-blue-600 transition-colors" aria-label="YouTube">
                          <YouTubeIcon className="w-5 h-5" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )
            })()}

            {/* Stats row — followers + positions. Hidden in the BOOKING LENS
                (client mode + logged-out public booking): a client booking a cut
                doesn't need network/hiring stats. Shown only in the community/
                hiring lenses for pros. */}
            {!bookingLens && (
              <div className="flex items-center gap-5 flex-wrap">
                <button
                  type="button"
                  onClick={() => setFollowersOpen(true)}
                  className="hover:opacity-70 transition-opacity"
                  aria-label="View followers"
                >
                  <span className="text-sm font-semibold tabular-nums">{followerCount}</span>
                  <span className="text-xs text-muted-foreground ml-1.5">followers</span>
                </button>
                {experience.length > 0 && (
                  <div>
                    <span className="text-sm font-semibold tabular-nums">{experience.length}</span>
                    <span className="text-xs text-muted-foreground ml-1.5">{experience.length === 1 ? "position" : "positions"}</span>
                  </div>
                )}
              </div>
            )}

            {/* Action row — lightweight icon+text buttons (Follow + Share live
                under the avatar; this row carries Message + Inquire). Save is
                an owner-only feature elsewhere; barbers don't save other barbers. */}
            {!isOwnProfile && (
              <div className="flex flex-wrap items-center gap-2">
                {/* In-app Message is a pro-network action — hidden in the booking
                    lens since clients reach barbers via Call / Text / WhatsApp. */}
                {!bookingLens && (
                  <button
                    onClick={handleMessage}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted transition-colors"
                    aria-label="Message"
                  >
                    <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.75} /> Message
                  </button>
                )}
                {/* Booking lens: direct contact actions when the barber shares a phone. */}
                {bookingLens && barberPhone && (() => {
                  const tel = barberPhone.replace(/[^\d+]/g, "")
                  const wa = barberPhone.replace(/\D/g, "")
                  return (
                    <>
                      <a href={`tel:${tel}`} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 dark:border-border bg-white dark:bg-background px-3 py-1.5 text-xs font-medium text-neutral-900 dark:text-foreground hover:bg-neutral-50 dark:hover:bg-muted transition-colors" aria-label="Call">
                        <Phone className="w-3.5 h-3.5" strokeWidth={1.75} /> Call
                      </a>
                      <a href={`sms:${tel}`} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 dark:border-border bg-white dark:bg-background px-3 py-1.5 text-xs font-medium text-neutral-900 dark:text-foreground hover:bg-neutral-50 dark:hover:bg-muted transition-colors" aria-label="Text">
                        <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.75} /> Text
                      </a>
                      <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 dark:border-border bg-white dark:bg-background px-3 py-1.5 text-xs font-medium text-neutral-900 dark:text-foreground hover:bg-neutral-50 dark:hover:bg-muted transition-colors" aria-label="WhatsApp">
                        <WhatsAppIcon className="w-3.5 h-3.5" /> WhatsApp
                      </a>
                    </>
                  )
                })()}
                {/* Inquire = owner-only hiring action. Gate on !bookingLens too:
                    a dual-role owner keeps currentUserRole==="owner" while browsing
                    in Client mode, so without it the button leaked into the client view. */}
                {currentUserRole === "owner" && !bookingLens && (
                  <button
                    onClick={handleInviteToApply}
                    className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:opacity-80 transition-opacity"
                    aria-label="Inquire"
                  >
                    <SendMark size={14} />
                    Inquire
                  </button>
                )}
              </div>
            )}

            {/* About — single source. pt-2 gives it a touch more air than the
                action rows above without falling back to the page-level gap. */}
            {profile.bio && (
              <div className="pt-2">
                <h3 className="text-sm font-medium mb-3 text-foreground">About</h3>
                <p className="text-sm leading-relaxed whitespace-pre-line">{profile.bio}</p>
              </div>
            )}

            </div>{/* /identity cluster */}

            {/* Open/closed banner — prominent "Currently closed · Opens …"
                cue above the booking flow (booking lens only). */}
            {bookingLens && shopHours.length > 0 && (
              <ShopHoursBanner hours={shopHours} className="w-full" />
            )}

            {/* Links — simple black icons */}
            {/* Booking flow (booking lens) — appointment experience above the
                portfolio so the Book CTA leads. */}
            {bookingLens && profile.bookings_enabled && services.length > 0 && (
              <div className="pb-2">
                <h3 className="text-sm font-medium mb-3 text-foreground">Book an appointment</h3>
                <StorefrontBooking
                  barberId={profile.id}
                  barberName={name}
                  avatarUrl={barberProfile?.avatar_url}
                  barberPhone={barberPhone ?? undefined}
                  bio={profile.bio}
                  services={services}
                  hideHeader
                  acceptsOnlinePayments={acceptsOnlinePayments}
                  depositsEnabled={depositsEnabled}
                  passProcessingToClient={passProcessingToClient}
                />
              </div>
            )}

            {/* Memberships — recurring plans the client can subscribe to. */}
            {bookingLens && profile.bookings_enabled && memberships.length > 0 && (
              <div className="pb-2">
                <h3 className="text-sm font-medium mb-3 text-foreground">Memberships</h3>
                <div className="space-y-2">
                  {memberships.map((m) => {
                    const per = m.interval === "quarter" ? "quarter" : "month"
                    return (
                      <div key={m.id} className="rounded-2xl bg-card p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground">{m.name}</p>
                            {m.description && <p className="text-xs text-muted-foreground mt-0.5">{m.description}</p>}
                            <p className="text-sm font-semibold text-foreground mt-1.5">
                              ${(m.price_cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              <span className="text-xs font-normal text-muted-foreground"> / {per}</span>
                            </p>
                          </div>
                          <Button
                            size="sm"
                            className="rounded-full shrink-0"
                            onClick={() => subscribeMembership(m.id)}
                            disabled={subscribingId === m.id}
                          >
                            {subscribingId === m.id ? "…" : "Subscribe"}
                          </Button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Portfolio — grid + carousel toggle, above Experience */}
            {portfolio.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-foreground">
                    Portfolio <span className="ml-1 text-muted-foreground/60 tabular-nums">{portfolio.length}</span>
                  </h3>
                  <div className="flex items-center gap-1 p-0.5 rounded-lg bg-muted">
                    <button
                      onClick={() => setPortfolioView("grid3")}
                      className={`p-1.5 rounded-md transition-colors ${portfolioView === "grid3" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                      aria-label="3-column grid"
                    >
                      <LayoutGrid className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setPortfolioView("grid2")}
                      className={`p-1.5 rounded-md transition-colors ${portfolioView === "grid2" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                      aria-label="2-column grid"
                    >
                      <Rows className="w-3.5 h-3.5" />
                    </button>
                    {/* Single view is desktop-only — on mobile the toggle is
                        just 3×3 (default) and 2×2. */}
                    <button
                      onClick={() => setPortfolioView("single")}
                      className={`hidden md:block p-1.5 rounded-md transition-colors ${portfolioView === "single" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                      aria-label="Single image view"
                    >
                      <Square className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {portfolioView === "grid3" && (
                  // 3-column tight grid (default).
                  <div className="grid grid-cols-3 gap-1">
                    {portfolio.map((img) => (
                      <button
                        key={img.id}
                        onClick={() => setLightboxImage(img.image_url)}
                        className="relative aspect-square overflow-hidden rounded-lg bg-muted hover:opacity-90 transition-opacity group"
                      >
                        <Image
                          src={img.image_url}
                          alt={img.caption || ""}
                          fill
                          className="object-cover group-hover:scale-105 transition-transform duration-300"
                          sizes="(max-width: 672px) 33vw, 224px"
                        />
                      </button>
                    ))}
                  </div>
                )}

                {portfolioView === "grid2" && (
                  // Pure 2-column grid — no overflow clip, all rows flow
                  // naturally with page scroll.
                  <div className="grid grid-cols-2 gap-2">
                    {portfolio.map((img) => (
                      <button
                        key={img.id}
                        onClick={() => setLightboxImage(img.image_url)}
                        className="relative aspect-square overflow-hidden rounded-2xl bg-muted hover:opacity-90 transition-opacity group"
                      >
                        <Image
                          src={img.image_url}
                          alt={img.caption || ""}
                          fill
                          className="object-cover group-hover:scale-105 transition-transform duration-300"
                          sizes="(max-width: 672px) 50vw, 320px"
                        />
                      </button>
                    ))}
                  </div>
                )}

                {portfolioView === "single" && (
                  // Single-column stack — one full-width image per row,
                  // scrolls with the page (no horizontal carousel, no
                  // forced viewport height that crops the last image).
                  <div className="space-y-3">
                    {portfolio.map((img) => (
                      <button
                        key={img.id}
                        onClick={() => setLightboxImage(img.image_url)}
                        className="relative w-full aspect-[4/5] overflow-hidden rounded-3xl bg-muted hover:opacity-90 transition-opacity"
                      >
                        <Image src={img.image_url} alt={img.caption || ""} fill className="object-cover" sizes="(max-width: 1024px) 100vw, 640px" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Work Locations (booking lens) — the barber's current shop + its
                live schedule, styled like Shop Experience. */}
            {bookingLens && activeShop && (() => {
              const open = getOpenState(shopHours)
              const today = new Date().getDay()
              const todayRow = shopHours.find((h) => h.day_of_week === today)
              const cityLine = displayLocation(activeShop.city, activeShop.state)
              return (
                <div>
                  <h3 className="text-sm font-medium mb-3 text-foreground">Hours, Availability &amp; Locations</h3>
                  <div className="rounded-3xl bg-card p-4 shadow-[0_1px_4px_rgba(0,0,0,0.05)]">
                    <div className="flex items-start gap-3">
                      {activeShop.cover_image ? (
                        <div className="w-10 h-10 rounded-lg overflow-hidden border border-border bg-muted flex-shrink-0 relative">
                          <Image src={activeShop.cover_image} alt={activeShop.name || "Shop"} fill className="object-cover" sizes="40px" />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-muted text-muted-foreground border border-border flex items-center justify-center flex-shrink-0">
                          <Building className="w-4 h-4" strokeWidth={1.75} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{activeShop.name}</p>
                        {cityLine && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                            <MapPin className="w-3 h-3 flex-shrink-0" /><span className="truncate">{cityLine}</span>
                          </p>
                        )}
                      </div>
                      {open && (
                        <span className={`text-[11px] font-medium rounded-full px-2 py-0.5 whitespace-nowrap ${open.open ? "bg-emerald-500/10 text-emerald-600" : "bg-muted text-muted-foreground"}`}>
                          {open.open ? "Open now" : "Closed"}
                        </span>
                      )}
                    </div>
                    {/* Weekly schedule */}
                    {shopHours.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                        {[1, 2, 3, 4, 5, 6, 0].map((d) => {
                          const row = shopHours.find((h) => h.day_of_week === d)
                          const isToday = d === today
                          return (
                            <div key={d} className={`flex items-center justify-between text-xs ${isToday ? "font-medium text-foreground" : "text-muted-foreground"}`}>
                              <span>{DAY_NAMES[d]}</span>
                              <span className="tabular-nums">
                                {!row || row.is_closed ? "Closed" : `${formatTime(row.open_time)} – ${formatTime(row.close_time)}`}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    )}
                    {open && !open.open && open.nextOpenLabel && (
                      <p className="mt-2 text-xs text-red-600 dark:text-red-400">{open.nextOpenLabel}. Book ahead!</p>
                    )}
                    {!shopHours.length && todayRow == null && (
                      <p className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">Hours not set yet.</p>
                    )}
                  </div>
                </div>
              )
            })()}

            {/* Experience — pastel logos, moved BELOW Portfolio */}
            {experience.length > 0 && (
              <div>
                <h3 className="text-sm font-medium mb-3 text-foreground">Shop Experience</h3>
                <div className="space-y-3">
                  {experience.map((exp, i) => {
                    const palette = SHOP_PASTELS[i % SHOP_PASTELS.length]
                    const dateRange = exp.is_current
                      ? `${exp.start_year ?? ""}${exp.start_year ? " — " : ""}Present`
                      : exp.start_year && exp.end_year
                        ? `${exp.start_year} — ${exp.end_year}`
                        : exp.start_year || exp.end_year || ""
                    const cityLine = displayLocation(exp.city, exp.state)
                    const linkedShop = exp.shop && !exp.shop.deleted_at ? exp.shop : null
                    const avatar = linkedShop?.cover_image ? (
                      <div className="w-10 h-10 rounded-lg overflow-hidden border border-border bg-muted flex-shrink-0 relative">
                        <Image src={linkedShop.cover_image} alt={linkedShop.name} fill className="object-cover" sizes="40px" />
                      </div>
                    ) : (
                      <div className={`w-10 h-10 rounded-lg ${palette.bg} ${palette.fg} border ${palette.ring} flex items-center justify-center flex-shrink-0`}>
                        <Building className="w-4 h-4" strokeWidth={1.75} />
                      </div>
                    )
                    const inner = (
                      <>
                        {avatar}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate">{exp.shop_name}</p>
                          {cityLine && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                              <MapPin className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{cityLine}</span>
                            </p>
                          )}
                        </div>
                        {dateRange && (
                          <span className="text-xs text-muted-foreground flex-shrink-0 tabular-nums whitespace-nowrap">{dateRange}</span>
                        )}
                      </>
                    )
                    return linkedShop?.slug ? (
                      <Link key={exp.id} href={`/shop/${linkedShop.slug}`} className="flex items-start gap-3 -mx-2 px-2 py-1.5 rounded-xl hover:bg-muted/50 transition-colors">
                        {inner}
                      </Link>
                    ) : (
                      <div key={exp.id} className="flex items-start gap-3">
                        {inner}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Social embeds last: the profile leads with who the barber is,
                booking, portfolio, hours and shops; the Instagram/TikTok
                embeds are supporting material below all of that. */}
            {socialEmbedSlides.length > 0 && (
              <BarberSocialEmbedRail slides={socialEmbedSlides} />
            )}

          </div>

        </div>
      </div>

      {/* Powered by BOOTH + minimal footer — public profile surfaces only
          (skipped in the embedded owner-dashboard view). */}
      {!embedded && (
        <>
          {/* "Powered by BOOTH" only on the non-booking (owner-internal) view;
              the client/booking lens stays clean per client-mode branding. */}
          {!bookingLens && (
            <p className="text-center text-[11px] text-muted-foreground mt-10 pb-8">Powered by BOOTH</p>
          )}
          <ClientFooter className="mt-10" />
        </>
      )}

      <FollowersModal
        open={followersOpen}
        onOpenChange={setFollowersOpen}
        barberProfileId={barberProfile.id}
      />

      <InquireModal
        open={inquireOpen}
        onClose={() => setInquireOpen(false)}
        barberId={barberProfile.id}
        barberName={barberProfile.full_name || "this barber"}
        ownerId={currentUserId}
        ownerShop={ownerShop}
      />
    </div>
  )
}
